import { useCallback, useMemo } from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { PagedQueryOptions } from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import {
  GroupedData,
  ColumnFilterOption,
} from '@elementary/common-ui/components/Table/types';
import {
  IncidentV2SortField,
  SortOrder,
} from '@elementary/common-ui/services/gql/types';
import { UrlFilters } from '@elementary/common-ui/hooks/useUrlFilters';
import { INCIDENT_LIST_PAGED } from '@elementary/common-ui/constants/queryKeys';
import { GROUP_BY_MAX_CARDINALITY } from '@elementary/common-ui/constants';
import { INCIDENT_LIST_GROUP_BY } from '@elementary/common-ui/constants/urlParams';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import { statusIconMap } from 'incidents/utils';
import useGroupedPagedQuery from 'shared/hooks/useGroupedPagedQuery';
import {
  COLUMN_TO_INCIDENT_FIELD_MAP,
  RESOLVED_STATUS,
  SEVERITY_DISPLAY,
  STATUS_GROUP_VALUES,
} from '../constants';
import { getIncidentsPagedQuery } from '../api';
import type {
  IncidentFilterOptionsGroup,
  IncidentListBaseFilters,
  IncidentListItem,
  IncidentsPagedResponse,
} from '../types';
import useGroupIncidentSummaries from './useGroupIncidentSummaries';
import IncidentGroupSeverityMetadata from './IncidentGroupSeverityMetadata';

const DEFAULT_GROUP_BY_COLUMN_ID = 'status';
const SEVERITY_COLUMN_ID = 'severity';
const ASSIGNEE_COLUMN_ID = 'assignee';

type Args = {
  search: string;
  urlFilters: UrlFilters | undefined;
  sortVariable:
    | { field: IncidentV2SortField; direction: SortOrder }
    | undefined;
  baseFilters: IncidentListBaseFilters;
  selectedEnvId: string | undefined;
  selectedIdsHash: string;
  filterOpts: IncidentFilterOptionsGroup[] | undefined;
};

const useGroupedIncidents = ({
  search,
  urlFilters,
  sortVariable,
  baseFilters,
  selectedEnvId,
  selectedIdsHash,
  filterOpts,
}: Args) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedGroupBy =
    searchParams.get(INCIDENT_LIST_GROUP_BY) || DEFAULT_GROUP_BY_COLUMN_ID;

  const onGroupByChange = useCallback(
    (next: string | null) => {
      setSearchParams((prev) => {
        const resolved = next || DEFAULT_GROUP_BY_COLUMN_ID;
        if (resolved === DEFAULT_GROUP_BY_COLUMN_ID) {
          prev.delete(INCIDENT_LIST_GROUP_BY);
        } else {
          prev.set(INCIDENT_LIST_GROUP_BY, resolved);
        }
        return prev;
      });
    },
    [setSearchParams],
  );

  const groupableColumnIds = useMemo(
    () =>
      Object.entries(COLUMN_TO_INCIDENT_FIELD_MAP)
        .filter(([columnId, field]) => {
          if (columnId === DEFAULT_GROUP_BY_COLUMN_ID) return true;
          const options =
            filterOpts?.find(
              (group) => group.field.toLowerCase() === field.toLowerCase(),
            )?.options ?? [];
          const count = options.length;
          return count > 0 && count <= GROUP_BY_MAX_CARDINALITY;
        })
        .map(([columnId]) => columnId),
    [filterOpts],
  );

  const groupByColumnId = groupableColumnIds.includes(requestedGroupBy)
    ? requestedGroupBy
    : DEFAULT_GROUP_BY_COLUMN_ID;

  const field = COLUMN_TO_INCIDENT_FIELD_MAP[groupByColumnId];

  const groupValues = useMemo<ColumnFilterOption[]>(() => {
    if (!field) return [];
    if (groupByColumnId === DEFAULT_GROUP_BY_COLUMN_ID) {
      return STATUS_GROUP_VALUES.map((value) => ({ label: value, value }));
    }
    const options =
      filterOpts?.find(
        (group) => group.field.toLowerCase() === field.toLowerCase(),
      )?.options ?? [];
    const mapped = options.map((option) => ({
      label: option.value,
      value: option.value,
      count: option.count,
    }));
    if (groupByColumnId === ASSIGNEE_COLUMN_ID) {
      return [
        { label: 'No Assignee', value: NO_ASSIGNEE_VALUE },
        ...mapped.filter((option) => option.value !== NO_ASSIGNEE_VALUE),
      ];
    }
    return mapped;
  }, [field, groupByColumnId, filterOpts]);

  const formatGroupTitle = useCallback(
    (value: string) => {
      if (groupByColumnId === SEVERITY_COLUMN_ID) {
        return SEVERITY_DISPLAY[value.toLowerCase()] ?? value;
      }
      if (
        groupByColumnId === ASSIGNEE_COLUMN_ID &&
        value === NO_ASSIGNEE_VALUE
      ) {
        return 'No Assignee';
      }
      return value;
    },
    [groupByColumnId],
  );

  const titleToValue = useMemo(() => {
    const map = new Map<string, string>();
    groupValues.forEach((option) => {
      map.set(formatGroupTitle(option.value), option.value);
    });
    return map;
  }, [groupValues, formatGroupTitle]);

  const groupKeys = useMemo(
    () => (field ? groupValues.map((option) => option.value) : []),
    [field, groupValues],
  );

  const showSeveritySummary = groupByColumnId !== SEVERITY_COLUMN_ID;

  const summariesByGroup = useGroupIncidentSummaries({
    field,
    groupValues: groupKeys,
    urlFilters,
    baseFilters,
    selectedEnvId,
    selectedIdsHash,
    enabled: showSeveritySummary && !!field,
  });

  const buildQueryOptions = useCallback(
    (
      value: string,
    ): PagedQueryOptions<IncidentListItem, IncidentsPagedResponse> => ({
      queryKey: [
        INCIDENT_LIST_PAGED,
        selectedEnvId,
        urlFilters,
        baseFilters.daysBack,
        search,
        selectedIdsHash,
        sortVariable,
        field,
        value,
      ],
      query: getIncidentsPagedQuery,
      variables: {
        filters: baseFilters,
        sort: sortVariable,
        limit: 50,
        incidentFilters: field
          ? [
              ...(urlFilters || []),
              { property: field, operator: 'equals', value: [value] },
            ]
          : urlFilters || [],
      },
      select: (data) => data.incidentsV2Paged,
      enabled: !!field && !!selectedEnvId,
      envId: selectedEnvId,
    }),
    [
      selectedEnvId,
      urlFilters,
      baseFilters,
      search,
      selectedIdsHash,
      sortVariable,
      field,
    ],
  );

  const {
    groupFetchers,
    getRows,
    fetchNextGroupPage,
    isAnyFetching,
    isInitialLoading,
  } = useGroupedPagedQuery<
    IncidentListItem,
    IncidentsPagedResponse,
    IncidentListItem
  >({
    groupKeys,
    buildQueryOptions,
    mapItems: (items) => items,
  });

  const groupedData = useMemo<GroupedData<IncidentListItem>[]>(
    () =>
      groupValues.map((option) => {
        const title = formatGroupTitle(option.value);
        const summary = summariesByGroup[option.value];
        const showGroupSeverityMetadata =
          showSeveritySummary &&
          !(
            groupByColumnId === DEFAULT_GROUP_BY_COLUMN_ID &&
            option.value === RESOLVED_STATUS
          );
        return {
          title,
          icon:
            groupByColumnId === DEFAULT_GROUP_BY_COLUMN_ID
              ? statusIconMap[option.value as keyof typeof statusIconMap]
              : undefined,
          count: summary?.total ?? option.count,
          metadataRenderer: showGroupSeverityMetadata
            ? () => (
                <IncidentGroupSeverityMetadata
                  bySeverity={summary?.bySeverity}
                />
              )
            : undefined,
          data: getRows(option.value),
        };
      }),
    [
      groupValues,
      formatGroupTitle,
      summariesByGroup,
      groupByColumnId,
      showSeveritySummary,
      getRows,
    ],
  );

  const onGroupScrollToBottom = useCallback(
    (groupTitle: string) => fetchNextGroupPage(titleToValue.get(groupTitle)),
    [fetchNextGroupPage, titleToValue],
  );

  const allIncidents = useMemo(
    () => groupedData.flatMap((group) => group.data),
    [groupedData],
  );

  const isFetching = isAnyFetching || isInitialLoading;
  const isGroupedByStatus = groupByColumnId === DEFAULT_GROUP_BY_COLUMN_ID;

  return {
    groupBy: groupByColumnId,
    onGroupByChange,
    groupableColumnIds,
    groupFetchers,
    groupedData,
    onGroupScrollToBottom,
    allIncidents,
    isFetching,
    isGroupedByStatus,
  };
};

export default useGroupedIncidents;
